import React, { useState } from "react";
import {
  Form,
  Upload,
  Button,
  Alert,
  Typography,
  message,
  Modal,
  Descriptions,
  Space,
} from "antd";
import {
  UploadOutlined,
  CloudUploadOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../configs";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const ModalUploadFile = ({ onClose }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Duplicate confirmation modal state
  const [dupModalVisible, setDupModalVisible] = useState(false);
  const [dupInfo, setDupInfo] = useState(null); // { existingRecord, fromFile }
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleFileChange = ({ fileList: newList }) => {
    const isExcel =
      newList.length > 0 &&
      newList[0].type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (!isExcel && newList.length > 0) {
      setErrorMsg("Hanya file Excel (.xlsx) yang diperbolehkan!");
      setFileList([]);
    } else {
      setErrorMsg("");
      setFileList(newList);
    }
  };

  // Step 1: Check duplicate before upload
  const handleUpload = async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) {
      setErrorMsg("Silakan pilih file Excel terlebih dahulu!");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const checkForm = new FormData();
      checkForm.append("file", file);

      const checkRes = await axiosInstance.post(
        "/portal/pasifik/check-duplicate",
        checkForm,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const { isDuplicate, existingRecord, fromFile } = checkRes.data;

      if (isDuplicate) {
        // Show confirmation dialog
        setDupInfo({ existingRecord, fromFile });
        setDupModalVisible(true);
        setLoading(false);
      } else {
        // No duplicate, proceed directly
        await doUpload(file);
      }
    } catch (error) {
      console.error("Check duplicate error:", error);
      setErrorMsg(
        error?.response?.data?.message ||
        "Gagal memeriksa duplikat. Silakan coba lagi."
      );
      setLoading(false);
    }
  };

  // Step 2: Actual upload
  const doUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      await axiosInstance.post(
        "/portal/pasifik/post-file/063239834304400",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      message.success("Dokumen berhasil diunggah!");
      setFileList([]);
      form.resetFields();
      onClose();
      setTimeout(() => window.location.reload(), 1200);
    } catch (error) {
      console.error("Upload error:", error);
      setErrorMsg(
        error?.response?.data?.message ||
        error.message ||
        "Terjadi kesalahan saat mengunggah file."
      );
    } finally {
      setLoading(false);
      setConfirmLoading(false);
    }
  };

  // User confirmed: replace existing data
  const handleConfirmReplace = async () => {
    const file = fileList[0]?.originFileObj;
    if (!file) return;
    setConfirmLoading(true);
    setDupModalVisible(false);
    setLoading(true);
    await doUpload(file);
  };

  // User cancelled: close dup modal, keep file selected
  const handleCancelReplace = () => {
    setDupModalVisible(false);
    setLoading(false);
  };

  const formatNomorAju = (val) =>
    val?.replace(/(\d{6})(\d{6})(\d{8})(\d{6})/, "$1-$2-$3-$4") || val || "-";

  const formatDate = (val) => {
    if (!val) return "-";
    return dayjs(val).isValid() ? dayjs(val).format("DD MMMM YYYY") : val;
  };

  return (
    <>
      <Form form={form} layout="vertical" className="kt-form">
        <Form.Item
          label={<Text strong>File Excel</Text>}
          required
          validateStatus={errorMsg ? "error" : ""}
        >
          <Upload
            beforeUpload={() => false}
            fileList={fileList}
            onChange={handleFileChange}
            accept=".xlsx"
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Pilih File Excel</Button>
          </Upload>
          {fileList.length > 0 && (
            <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              File terpilih: {fileList[0].name}
            </Text>
          )}
        </Form.Item>

        {errorMsg && (
          <Alert
            type="error"
            message={errorMsg}
            style={{ marginBottom: 12 }}
            showIcon
          />
        )}

        <div style={{ textAlign: "right", marginTop: 24 }}>
          <Button
            icon={<CloseOutlined />}
            style={{ marginRight: 8 }}
            onClick={onClose}
            disabled={loading}
          >
            Batal
          </Button>
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={loading}
            onClick={handleUpload}
          >
            {loading ? "Memeriksa..." : "Unggah"}
          </Button>
        </div>
      </Form>

      {/* ── Duplicate Confirmation Modal ── */}
      <Modal
        open={dupModalVisible}
        onCancel={handleCancelReplace}
        footer={null}
        centered
        width={520}
        closable={!confirmLoading}
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: "#faad14", fontSize: 18 }} />
            <span style={{ color: "#faad14", fontWeight: 600 }}>
              Dokumen Sudah Ada
            </span>
          </Space>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            File yang Anda upload sudah pernah diimpor sebelumnya. Data yang
            ada akan <b>diganti</b> jika Anda melanjutkan.
          </Text>
        </div>

        <Descriptions
          bordered
          size="small"
          column={1}
          style={{ marginBottom: 20 }}
          title={
            <Text strong style={{ color: "#1890ff" }}>
              Data Saat Ini di Database
            </Text>
          }
        >
          <Descriptions.Item label="Kode Dokumen">
            {dupInfo?.existingRecord?.kode_dokumen || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="No. Pengajuan">
            <b>{formatNomorAju(dupInfo?.existingRecord?.nomor_aju)}</b>
          </Descriptions.Item>
          <Descriptions.Item label="No. Pendaftaran">
            {dupInfo?.existingRecord?.nomor_daftar || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Tanggal Pendaftaran">
            {formatDate(dupInfo?.existingRecord?.tanggal_daftar)}
          </Descriptions.Item>
        </Descriptions>

        <div style={{ textAlign: "right" }}>
          <Space>
            <Button
              icon={<CloseOutlined />}
              onClick={handleCancelReplace}
              disabled={confirmLoading}
            >
              Batal, Jangan Ganti
            </Button>
            <Button
              type="primary"
              danger
              icon={<CheckCircleOutlined />}
              loading={confirmLoading}
              onClick={handleConfirmReplace}
            >
              Ya, Ganti Data
            </Button>
          </Space>
        </div>
      </Modal>
    </>
  );
};

export default ModalUploadFile;
